import { getDictionary } from 'lib/apiClient';
import { BrandCode, Locale } from 'lib/config/config';
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import defaultDictionary from 'resources/defaultDictionary';
import { useDictionaryApi } from './useDictionaryApi';

const DEFAULT_LOCALE = {
	country: 'USA',
	language: 'en',
	currency: 'USD',
	office: 'MIA',
	market: 'usa',
	nameCountry: 'UNITED STATES',
	phoneNumber: '866-562-7625',
	currencies: ['USD'],
	path: '/',
	countryAlpha2Code: 'us',
	code: 'en_US',
};

export type Dictionary = Record<string, string>;

interface AppProviderProps {
	children: ReactNode;
	locale: Locale;
	brandCode: BrandCode;
}

interface AppContextState {
	dictionary: Dictionary;
	brandCode: BrandCode;
	locale: Locale;
}

const AppContext = createContext<AppContextState>({
	dictionary: {},
	brandCode: BrandCode.RoyalCaribbean,
	locale: DEFAULT_LOCALE,
});

export const AppProvider = ({ children, locale, brandCode }: AppProviderProps) => {
	const { data: dictionaryData, isLoading } = useDictionaryApi(locale.language, defaultDictionary);
	const [dictionary, setDictionary] = useState<Dictionary>(dictionaryData);

	useEffect(() => {
		if (!isLoading) {
			setDictionary(dictionaryData);
		}
	}, [isLoading, dictionaryData]);

	useEffect(() => {
		getDictionary(locale.language).then((dictionary) => setDictionary(dictionary));
	}, [locale.language]);

	const value = useMemo(
		() => ({
			dictionary,
			brandCode,
			locale,
		}),
		[dictionary, brandCode, locale],
	);

	return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => useContext(AppContext);
